// P-METADATA-EXPAND-A — milestone_events: types + persistence helper.
//
// MilestoneEvent is a separate event stream from child_attempts: the
// milestone is observable at the POINT OF DERIVATION (skill_secured at
// deriveMasteryBand, chapter_complete at chapterComplete, etc.) instead
// of reconstructed from attempts later. The parent-dashboard Tier 3
// read shape ("what happened recently?") wants a chronological stream
// — re-aggregating attempts every read is expensive and racy.
//
// THIS PR ships the SCHEMA + WRITE HELPER only. No derivation site is
// wired — every event type (skill_secured, chapter_complete,
// mascot_evolved, story_unlocked, badge_earned, streak_milestone) is
// deferred to P-METADATA-EXPAND-B because each needs an architectural
// decision on its derivation hook (post-write re-aggregate vs reactive
// listener vs deferred batch). The schema is bedrock NOW so when
// PART-B wires the first event, no ALTER on the (by then larger) event
// log is required.
//
// Append-only like child_attempts (migration 0006 RLS: SELECT + INSERT,
// no UPDATE / DELETE). An event is immutable evidence.

// supabaseClient.js is untyped JS (same as attemptLogger).
import { supabase } from "@/supabaseClient.js";
import { captureSupabaseError } from "@/lib/sentry";
import { isCircuitOpen, recordSuccess, recordFailure } from "./networkCircuitBreaker";
import { fromRow } from "./milestoneRow";

const TABLE = "milestone_events";
const MAX_RETRIES = 3;
const RETRY_BASE_MS = 150;

// ── Event taxonomy ───────────────────────────────────────────────────

/** The 6 milestone event types from the parent-dashboard research.
 *  Extensible by adding to the migration 0006 CHECK list (the
 *  taxonomy is owned by the parent-dashboard product, not the engine,
 *  so additions are a product decision + migration). */
export type MilestoneEventType =
  | "skill_secured"       // a skill moved into masteryBand secure-or-fluent
  | "chapter_complete"    // chapter milestone bundle reached
  | "story_unlocked"      // earned-story chooser surfaced
  | "badge_earned"        // MA-892 — one row per badge record (buildBadgeEarnedEvent; src/lib/engine/badges/persistence.ts)
  | "mascot_evolved"      // mascot stage transition
  | "streak_milestone"    // parent-visible streak milestone (no child UI)
  // P-EXIT-SESSION — the trail-completion marker. Fired ONCE when a kid
  // reaches the session recap (finalizeSession in each session screen),
  // NOT on any-attempt-fires. completedSubjectsToday (trailState.ts)
  // derives the /play "Today's trail" N/3 counter from these events, so a
  // kid who answers 1-of-15 and exits before the recap does NOT advance
  // the strand. Admitted by the 0007 CHECK-constraint widening.
  | "session_completed"
  // MA-176 — fired once per COMPLETED read of an earned story (StoryReader's
  // terminal "The End" tap). Distinct from story_unlocked (the earn event)
  // and from markStoryRead/completedStories (the local-only ✓ boolean).
  // readLogFromEvents (src/engine/storyReadLog.ts) derives per-slug
  // {count, lastReadAt} from these. Admitted by the 0018 CHECK-constraint
  // widening.
  | "story_read"
  // MA-177 — one per heart-toggle tap (reader finish page + shelf tiles).
  // Reversible: a favorite_unset after a favorite_set un-favorites (never
  // deletes the prior row — append-only, same as every other event type).
  // favoriteSlugsFromEvents/favoriteStoriesFromEvents (src/engine/
  // favoriteStore.ts) derive "is this slug currently favorited" as
  // last-event-wins by event_at. Admitted by the 0019 CHECK-constraint
  // widening.
  | "favorite_set"
  | "favorite_unset"
  // MA-256 slice 1 — fired once per StoryReader mount (the "kid tapped a
  // book to open it" signal). The ONLY new event type that slice shipped;
  // book_impression (shelf-render exposure, THIS slice) is still only
  // wired for one shelf surface — see below. The playback funnel
  // (book_start/max_page_reached/audio_completed/skip|pause/exit_page),
  // and a dedicated reopen/replay_complete/days_since_first_exposure wire
  // event + richer reward-source enum are ALL deferred to later slices
  // (see MA-256's BACKLOG slices-remaining ledger). "Voluntary reopen" is
  // derived client-side (src/engine/bookOpenLog.ts) and server-side
  // (founder_analytics_counts) from REPEATED book_opened rows for the same
  // (child, slug) rather than shipping a separate `reopen` event — a
  // second/third/etc. open of an already-opened slug IS a reopen by
  // construction, so no extra wire event is needed to detect it. Admitted
  // by the 0028 CHECK-constraint widening.
  | "book_opened"
  // MA-256 book-impression slice — fired when a book tile is actually
  // shown to the kid on a shelf surface, so "rejected" (impressed, never
  // opened) is distinguishable from "never seen" (no impression event
  // exists). The first slice wired exactly ONE of the five shelf_surface
  // values named in the acceptance text — "unlock-choice"
  // (DayCompleteCelebration's always-exactly-3-tile reward picker: bounded,
  // zero pagination/virtualization risk, and the actual reward-loop peak).
  // The book-impression-remaining-surfaces slice added a SECOND —
  // "next-picks" (StoryLibraryScreen's day-seeded "Your next picks" curated
  // row, ~10 tiles: bounded count, real IntersectionObserver-gated
  // visibility per tile — see shouldRecordScrollRowImpression in
  // src/engine/bookImpressionLog.ts). "collection" (the subject-collection
  // rows sharing the same TileScrollRow component), "grid"
  // (StoryLibraryScreen's paginated/growing "All stories" catalog, ~1,400+
  // books), and "favorites" (ReadTab's "Books you love" row, unbounded
  // count) are still deferred — each is a bigger volume-control surface
  // than either shipped producer takes on — see the
  // book-impression-remaining-surfaces ledger token. A founder-analytics
  // rollup (the impressed-vs-opened join) is ALSO deferred
  // (book-impression-rollup token): these slices only make the underlying
  // data exist, via src/engine/bookImpressionLog.ts's pure derivation.
  // Admitted by the 0030 CHECK-constraint widening (unchanged by this
  // slice — shelfSurface lives in contextData jsonb, not a CHECK-
  // constrained column).
  | "book_impression"
  // MA-238 event-emission-migration slice — fired once per mount of a
  // teacher's SendHomeCardPanel (src/routes/teacher.tsx): the "a teacher
  // generated/opened this student's send-home card" signal (feeds MA-240's
  // usage analytics). Written by a TEACHER session, not a parent/kid one —
  // unlike every other event type above, there is deliberately NO client-
  // side builder/logger for this type in this file: a plain table upsert
  // from a teacher session is rejected by the parent-only
  // milestone_events_insert RLS policy (0006), so the write instead goes
  // through the SECURITY DEFINER `log_send_home_generated_event` RPC
  // (migration 0031, src/lib/teacher/sendHomeEvents.ts), which re-derives
  // live class_enrollments/classes linkage rather than trusting a
  // (viewer-scoped, receipt-view-purposed) student_access row — see 0031's
  // header for why a bare student_access check would have been a privilege-
  // creep bug, mirroring 0027's assign_skill_to_student precedent. This
  // union member exists so `fetchMilestoneEventsForChild` reads (e.g. a
  // future MA-240 parent/founder-dashboard rollup) can type these rows;
  // `milestoneEventKey`'s switch has no case for it (falls to `default`,
  // never exercised in practice since nothing constructs a MilestoneEvent
  // for this type client-side — the RPC computes its own key server-side,
  // using the SAME derivation format for consistency). Admitted by the
  // 0031 CHECK-constraint widening. `send_home_redeemed` (the item's OTHER
  // named event) is deliberately NOT added yet — it has no trigger point
  // until MA-241's redemption route exists; see 0031's header.
  | "send_home_generated"
  // MA-331 — fired once per confirmed mid-session exit (the kid taps Exit on
  // ExitConfirmDialog and the session has NOT reached the recap). Mutually
  // exclusive with session_completed for a given runId: each screen's onExit
  // handler only fires this when sessionDoneRef hasn't already been set by
  // the recap path. Feeds the canonicalEvidence isAbandonment limb + (a
  // deferred slice's) founder_analytics_counts() abandonment-rate rollup.
  // Admitted by the 0034 CHECK-constraint widening.
  | "session_exit"
  // MA-624 — fired once per SessionRecap DifficultyFeedback tap (the kid's
  // "how was that?" self-report). A derivation signal, not a reward, mirroring
  // buildSessionExitEvent's posture: rewardType null, child-invisible,
  // parent-visible. SELF-REPORT, NOT EVIDENCE — see tierWithSessionFeedback
  // (selectNextItem.ts) and Progress.sessionFeedback (App/progress.ts) for the
  // only consumer of the underlying value; this event is the parent-visible
  // ledger record only. NOTE: requires the CHECK-constraint widening in
  // supabase/migrations/0051_session_feedback_event.sql (mirrors 0034) —
  // drafted, not yet applied to prod (docs/PROD_APPLIED.md marks it ⚠️
  // Founder to apply). Until applied, INSERTs against a real milestone_events
  // table are rejected with 23514; the write helper below fails soft
  // (best-effort retry, same as every other logXxx in this file) — but,
  // mirroring session_exit's posture exactly (MathSession.tsx's
  // ExitConfirmDialog->appendLocalMilestone call), SessionRecap's onChange
  // ALSO mirrors the tap locally via appendLocalMilestone BEFORE the cloud
  // attempt, and "session_feedback" is in outboxDrain.ts's
  // DRAINABLE_MILESTONE_TYPES — so a pre-apply (or offline) tap is not lost,
  // only delayed until the next verified-online drain after 0051 is applied.
  | "session_feedback"
  // MA-300 — fired once per (child, skill, secure reach) at the moment a
  // session screen records the reach's FIRST next-day independent attempt
  // (confirmAttemptOutcome in progression.ts — the MA-297 confirm rule's own
  // internals, never a second rule); contextData.passed = that attempt's
  // correctness. Feeds founder_analytics_counts()'s confirm_7d block (the
  // +1d confirm pass-rate the MA-DEC-0721 #1 revert lines read). Telemetry,
  // not a reward: rewardType null, invisible to child AND parent. Requires
  // the CHECK-constraint widening in supabase/migrations/0054 (founder
  // applies; docs/PROD_APPLIED.md marks it pending) — until then the cloud
  // INSERT is rejected (23514) and fails soft, while the local mirror
  // (appendLocalMilestone, written FIRST) plus outboxDrain's
  // DRAINABLE_MILESTONE_TYPES carry it to the cloud after the apply.
  | "confirm_attempt";

// MA-245 (nightly-green-confirm slice) — event types that must NEVER count
// toward the shared networkCircuitBreaker's failure tally. The qualifying
// property is NARROW and mechanical: the type can fire N times per single
// render/mount with ZERO corresponding kid action (DayCompleteCelebration
// fires one `book_impression` per story tile shown; StoryLibraryScreen's
// IntersectionObserver fires one per scrolled-into-view tile) — NOT "low
// stakes" or "analytics" in general. Every other type (favorite_set/unset,
// story_read, book_opened, session_completed, story_unlocked, ...) fires at
// MOST ONCE per discrete user action, so a failure there IS the same
// one-write-per-action signal child_attempts failures already trip the
// breaker on — exempting those would mask a real outage.
//
// Root cause this closes (e2e/38-day-complete-write-failure.spec.ts): on
// DayCompleteCelebration mount, 3 book_impression writes (one per tile) plus
// the kid's own story_unlocked write can ALL fail during a network blip —
// 3 impression failures + 1 real failure hits FAILURE_THRESHOLD (4) on the
// real write, tripping the breaker for the full COOLDOWN_MS even though the
// backend had already recovered by the time the kid tapped "retry." The
// kid's deliberate retry was then silently swallowed by isCircuitOpen()
// without ever reaching the (by-then-healthy) network — indistinguishable
// in the UI from a real failure, burning one of MAX_PICK_ATTEMPTS for
// nothing. Adding a new bulk-fired analytics type here is a real decision,
// not a rubber stamp — only add it if it shares the exact N-per-render/
// zero-kid-action property above.
//
// MA-300 (RED M1) — `confirm_attempt` qualifies on the same property from a
// different direction: it is pure telemetry written with ZERO kid action, and
// until the founder applies 0054 every write is refused DETERMINISTICALLY
// (23514), while the outbox drain re-sends every pending row on each tick
// (app mount, every 5 min, foreground, online). Counted, N >= 4 pending rows
// opened the breaker on a single drain — the child then saw the
// "couldn't save your progress" toast and a reward-story pick burned a retry
// against a healthy backend (RED harness, e2e/38 class). Its failures can
// never be the signal a kid's retry depends on, so they never count.
const BREAKER_EXEMPT_EVENT_TYPES: ReadonlySet<MilestoneEventType> = new Set([
  "book_impression",
  "confirm_attempt",
]);

// MA-300 (RED M1/S6) — types whose cloud write is refused until a
// founder-applied migration lands (0054 admits confirm_attempt). After ONE
// permanent failure of such a type, every further write of that type in this
// app session returns false at once, without touching the network or Sentry:
// the rows stay in the local mirror, unconfirmed, and the next app session's
// drain tries again (one Sentry report per session at most, one 4-try upload
// per session instead of one per pending row per drain tick). Nothing else is
// affected: the breaker never counts these (above), and no other type is
// latched.
const QUIET_UNTIL_APPLIED_EVENT_TYPES: ReadonlySet<MilestoneEventType> = new Set([
  "confirm_attempt",
]);
const quietTypesStuckThisSession = new Set<MilestoneEventType>();

/** The five shelf surfaces the book_impression acceptance text names.
 *  Only "unlock-choice" has a live producer today (DayCompleteCelebration) —
 *  the rest are exported now so future slices share one type rather than
 *  re-deriving the union. */
export type ShelfSurface =
  | "next-picks"
  | "collection"
  | "grid"
  | "favorites"
  | "unlock-choice";

/** Reward grant attached to the event. null = the event fired without
 *  granting a reward (e.g. an internal skill_secured event that doesn't
 *  grant a reward). The two visibility flags decide whether the
 *  kid sees the celebration screen and whether the parent dashboard
 *  shows the event card. */
export type RewardType =
  | "sticker"             // schema member; no live producer (sticker UI pruned in P-PRUNING-WAVE)
  | "story_unlock"
  | "mascot_stage"
  | "certificate"
  | "badge";

export interface MilestoneEvent {
  /** Server-generated UUID. Omitted on writes (DB default fills). */
  id?: string;
  /** MA-14: deterministic idempotency key (migration 0015). Set by
   *  toRow via milestoneEventKey. Never set by callers directly.
   *  undefined on pre-0015 legacy rows read back via fromRow. */
  eventKey?: string;
  childId: string;
  eventType: MilestoneEventType;
  /** ISO-8601. Omitted on writes (DB default `now()` fills) but
   *  always set on reads. Writers MAY pass an explicit timestamp for
   *  back-dated events (e.g. a deferred derivation catching up). */
  eventAt?: string;
  /** Skills that produced this event. For skill_secured: the single
   *  skill that transitioned. For chapter_complete: every milestone +
   *  support skill in the chapter (so the parent dashboard can join
   *  back to specific evidence). Empty array allowed (mascot_evolved
   *  may not have a single skill source). */
  sourceSkillIds: string[];
  /** Optional path / chapter slug the event belongs to. null when the
   *  event isn't path-scoped (mascot_evolved, streak_milestone). */
  sourcePathId: string | null;
  /** Reward grant, if any. null = no reward (the event is internal /
   *  parent-dashboard-only). */
  rewardType: RewardType | null;
  /** Drives whether the kid sees the celebration screen. Default
   *  false — the writer must explicitly opt in. */
  rewardVisibleToChild: boolean;
  /** Drives whether the parent dashboard shows the event card.
   *  Default true — almost every milestone is parent-relevant. */
  rewardVisibleToParent: boolean;
  /** Event-type-specific payload (e.g. pre/post mastery band, chapter
   *  slug, mascot stage). Schema-on-read so future event types don't
   *  require a migration. */
  contextData: Record<string, unknown>;
}

// ── Idempotency key (MA-14) ──────────────────────────────────────────

/** Deterministic idempotency key for a milestone_events row.
 *  Derivation is event_type-specific so each event type has a natural
 *  deduplication scope:
 *  - session_completed: childId:session_completed:<runId>
 *    (one completion per session run; runId is in contextData.runId)
 *  - session_exit: childId:session_exit:<runId>
 *    (one exit per session run; mutually exclusive with session_completed
 *    for the same runId — a run either reaches recap or is exited)
 *  - story_unlocked: childId:story_unlocked:<slug>
 *    (one earn per story per child; slug is in contextData.slug)
 *  - story_read: childId:story_read:<slug>:<runId>
 *    (one row per COMPLETED read; runId is a fresh id per StoryReader mount
 *    so two distinct reads of the same slug each get their own row, while a
 *    retried write of the SAME finish — same mount, same runId — collapses)
 *  - favorite_set / favorite_unset: childId:<eventType>:<slug>:<runId>
 *    (one row per TOGGLE TAP; runId is a fresh id per tap so two distinct
 *    taps each get their own row — needed since a slug's favorited state can
 *    flip back and forth, unlike story_unlocked's one-row-per-slug — while a
 *    retried write of the SAME tap — same runId — collapses)
 *  - badge_earned (MA-892): childId:badge_earned:<badgeId>
 *    (one row per badge RECORD; the stage or repeat instance is part of
 *    the badge id — skill:<id>:silver, skill:<id>:gold, days:7#2 — so a
 *    skill's silver and gold, or two holds of the 7-day badge, are two
 *    rows, while a re-write of the same record collapses)
 *  - default: childId:<eventType>:<sourcePathId|"none">
 *    (covers skill_secured, chapter_complete, mascot_evolved, etc.) */
function milestoneEventKey(e: MilestoneEvent): string {
  switch (e.eventType) {
    case "session_completed": {
      const runId = e.contextData.runId as string | undefined;
      return `${e.childId}:session_completed:${runId ?? "unknown"}`;
    }
    case "session_exit": {
      // One row per session run, mirroring session_completed — the two
      // types are mutually exclusive per runId (a run either reaches recap
      // or is exited, never both), so no collision risk between them.
      const runId = e.contextData.runId as string | undefined;
      return `${e.childId}:session_exit:${runId ?? "unknown"}`;
    }
    case "story_unlocked": {
      const slug = e.contextData.slug as string | undefined;
      return `${e.childId}:story_unlocked:${slug ?? "unknown"}`;
    }
    case "story_read": {
      const slug = e.contextData.slug as string | undefined;
      const runId = e.contextData.runId as string | undefined;
      return `${e.childId}:story_read:${slug ?? "unknown"}:${runId ?? "unknown"}`;
    }
    case "favorite_set":
    case "favorite_unset": {
      const slug = e.contextData.slug as string | undefined;
      const runId = e.contextData.runId as string | undefined;
      return `${e.childId}:${e.eventType}:${slug ?? "unknown"}:${runId ?? "unknown"}`;
    }
    case "book_opened": {
      const slug = e.contextData.slug as string | undefined;
      const runId = e.contextData.runId as string | undefined;
      return `${e.childId}:book_opened:${slug ?? "unknown"}:${runId ?? "unknown"}`;
    }
    case "session_feedback": {
      // One row per session run, mirroring session_exit/session_completed.
      // Deliberately FIRST-WINS, not last-wins: the key carries NO `level`,
      // so if a kid taps "Too easy" then changes their mind and taps "Just
      // right" within the same recap, the upsert's ignoreDuplicates keeps
      // the FIRST tap's row and silently drops the second — same posture as
      // Progress.sessionFeedback's single in-memory slot being overwritten
      // by the LAST tap (the engine nudge always reflects the latest tap;
      // only the milestone ledger's row is first-wins). This asymmetry is
      // intentional: the ledger is a lightweight "did the kid rate this
      // run" signal, not a full edit history, and re-keying on `level` would
      // let one run contribute multiple ledger rows for a rapid multi-tap.
      const runId = e.contextData.runId as string | undefined;
      return `${e.childId}:session_feedback:${runId ?? "unknown"}`;
    }
    case "confirm_attempt": {
      // One row per (child, skill, secure reach): the FIRST next-day
      // independent attempt decides, so a retried or re-emitted write for the
      // same reach (a resumed run, a second device) collapses first-wins.
      const skillId = e.sourceSkillIds[0];
      const reachDay = e.contextData.reachDay as string | undefined;
      return `${e.childId}:confirm_attempt:${skillId ?? "unknown"}:${reachDay ?? "unknown"}`;
    }
    case "book_impression": {
      // shelfSurface is part of the key (not just slug+runId) so a future
      // surface reusing this event type can never collide with
      // unlock-choice's rows even under a coincidental runId overlap.
      const slug = e.contextData.slug as string | undefined;
      const surface = e.contextData.shelfSurface as string | undefined;
      const runId = e.contextData.runId as string | undefined;
      return `${e.childId}:book_impression:${slug ?? "unknown"}:${surface ?? "unknown"}:${runId ?? "unknown"}`;
    }
    case "badge_earned": {
      // MA-892 (KID-JOURNEY D-KJ-11): the default key below would be
      // childId:badge_earned:none for every badge, and the upsert's
      // ignoreDuplicates would drop every badge after a child's first.
      const badgeId = e.contextData.badgeId as string | undefined;
      return `${e.childId}:badge_earned:${badgeId ?? "unknown"}`;
    }
    default:
      return `${e.childId}:${e.eventType}:${e.sourcePathId ?? "none"}`;
  }
}

// ── camelCase MilestoneEvent → snake_case row ────────────────────────

function toRow(e: MilestoneEvent): Record<string, unknown> {
  const row: Record<string, unknown> = {
    child_id: e.childId,
    event_type: e.eventType,
    source_skill_ids: e.sourceSkillIds,
    source_path_id: e.sourcePathId,
    reward_type: e.rewardType,
    reward_visible_to_child: e.rewardVisibleToChild,
    reward_visible_to_parent: e.rewardVisibleToParent,
    context_data: e.contextData,
    // MA-14: idempotency key — derived deterministically so a retry
    // of the same logical event produces the same key → DO NOTHING.
    event_key: milestoneEventKey(e),
  };
  // event_at + id are server-defaulted; only include if the writer
  // passed them explicitly (back-dated catch-up writes, etc.).
  if (e.eventAt) row.event_at = e.eventAt;
  if (e.id) row.id = e.id;
  return row;
}

// ── snake_case row → camelCase MilestoneEvent (milestoneRow.ts, MA-874 D-1101-23) ──

const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms));

/** Persist one milestone event. Same retry policy as logAttempt
 *  (linear backoff, 3 retries, swallow on permanent failure — the
 *  event is best-effort: a missed milestone shouldn't crash the
 *  session). Returns true on success, false on permanent failure.
 *  `client` is test-only (mirrors attemptLogger's injectable-client
 *  convention) — every real call site relies on the default. */
export async function logMilestoneEvent(
  event: MilestoneEvent,
  client: Pick<typeof supabase, "from"> = supabase,
): Promise<boolean> {
  // P0: skip the network while the breaker is open — the local-first
  // mirror already holds this row; stop hammering a dead backend. This
  // read-gate stays UNCONDITIONAL (even exempt types skip network during
  // a real open breaker) — only the WRITE side below (recordFailure) is
  // type-conditional.
  if (isCircuitOpen()) return false;
  // MA-300: a quiet type already refused once this session waits for the
  // next session (see QUIET_UNTIL_APPLIED_EVENT_TYPES).
  if (quietTypesStuckThisSession.has(event.eventType)) return false;
  const quiet = QUIET_UNTIL_APPLIED_EVENT_TYPES.has(event.eventType);
  const countsTowardBreaker = !BREAKER_EXEMPT_EVENT_TYPES.has(event.eventType);
  const row = toRow(event);
  for (let i = 0; i <= MAX_RETRIES; i++) {
    try {
      // MA-14: upsert with DO NOTHING on event_key conflict so a retried
      // write that already landed server-side is a safe no-op.
      const { error } = await client.from(TABLE).upsert(row, {
        onConflict: "event_key",
        ignoreDuplicates: true,
      });
      if (!error) { recordSuccess(); return true; }
      if (i === MAX_RETRIES) {
        if (countsTowardBreaker) recordFailure();
        if (quiet) quietTypesStuckThisSession.add(event.eventType);
        captureSupabaseError(`milestone_events upsert failed after ${MAX_RETRIES} retries`, error, {
          childId: event.childId,
          eventType: event.eventType,
        });
        return false;
      }
    } catch (e) {
      if (i === MAX_RETRIES) {
        if (countsTowardBreaker) recordFailure();
        if (quiet) quietTypesStuckThisSession.add(event.eventType);
        captureSupabaseError(`milestone_events upsert threw after ${MAX_RETRIES} retries`, e, {
          childId: event.childId,
          eventType: event.eventType,
        });
        return false;
      }
    }
    await sleep(RETRY_BASE_MS * (i + 1));
  }
  return false;
}

// ── P-EXIT-SESSION — session-completed marker ────────────────────────

/** Trail subjects a completed session can advance. MIRRORS
 *  trailState.TrailSubject — kept as a local literal union (not an
 *  import) so milestoneLogger has no dependency on the presentation
 *  layer. FoundationSession maps its focal skill's strand to this via
 *  subjectForSkill ("foundation" reading skills → "reading"); the
 *  "foundation" literal is intentionally NOT a value here (foundation
 *  is a delivery mode, not a trail row). */
type SessionCompletedSubject = "math" | "reading" | "mixed";

interface SessionCompletedArgs {
  /** The CANONICAL cloud students.id (the FK/RLS target) — resolve via
   *  ensureActiveStudentId at the call site, NOT the local activeChildId. */
  childId: string;
  /** Client-generated run id grouping this session's attempts
   *  (runIdRef.current at the call site). */
  sessionRunId: string;
  /** Which trail row this completion advances. */
  subject: SessionCompletedSubject;
  /** How many attempts the kid logged in this run (telemetry; lets the
   *  parent dashboard distinguish a 3-question warm-up from a 15-question
   *  session without re-joining child_attempts). */
  attemptsInRun: number;
  /** Distinct skill ids the session touched (deduped here). Stored on the
   *  source_skill_ids column AND mirrored into context_data per the brief. */
  skillIds: string[];
  /** Optional path/chapter slug the session belongs to. */
  sourcePathId?: string | null;
  /** P-MILESTONE-DERIVATION — did this session clear the mastery tier
   *  (isMasteryTier, computed at the call site)? Mirrored into
   *  context_data.mastery so dayMasteryTier (trailState) can require all
   *  three strands to be mastery-tier before the day earns the "Special
   *  Edition" story signal. Defaults to false (a normal completion).
   *  Omitted from context_data when false so the marker shape matches the
   *  pre-mastery markers already in prod (schema-on-read: absent ⟹ false). */
  masteryTier?: boolean;
}

/** Pure builder for the session-completed MilestoneEvent. Split out so the
 *  shape is unit-testable without a Supabase round-trip. The marker is a
 *  derivation signal, not a reward: rewardType null, child-invisible
 *  (no celebration is driven off it), parent-visible (a completed session
 *  is a parent-relevant "what happened today" signal, matching the table
 *  default). */
export function buildSessionCompletedEvent(args: SessionCompletedArgs): MilestoneEvent {
  const skillIds = [...new Set(args.skillIds)];
  const contextData: Record<string, unknown> = {
    // subject is THE field completedSubjectsToday reads to decide which
    // trail row to advance. runId + attemptsInRun + skillIds are
    // telemetry (the brief requires them in context_data).
    subject: args.subject,
    runId: args.sessionRunId,
    attemptsInRun: args.attemptsInRun,
    skillIds,
  };
  // mastery is the per-session "Special Edition" signal. Stamped only when
  // true so existing (pre-mastery) markers keep their shape; dayMasteryTier
  // reads `=== true`, so an absent flag is correctly treated as non-mastery.
  if (args.masteryTier === true) contextData.mastery = true;
  return {
    childId: args.childId,
    eventType: "session_completed",
    sourceSkillIds: skillIds,
    sourcePathId: args.sourcePathId ?? null,
    rewardType: null,
    rewardVisibleToChild: false,
    rewardVisibleToParent: true,
    contextData,
  };
}

/** Fire the trail-completion marker. Call AFTER the session aggregate save
 *  in finalizeSession (recap-reached), never on exit-before-recap. Best-
 *  effort like logMilestoneEvent (retry + swallow); returns true on
 *  success. */
export async function logSessionCompleted(args: SessionCompletedArgs): Promise<boolean> {
  return logMilestoneEvent(buildSessionCompletedEvent(args));
}

// ── MA-331 — quit/abandonment marker ─────────────────────────────────

interface SessionExitArgs {
  /** The CANONICAL cloud students.id — same rule as every other milestone
   *  event (resolve via ensureActiveStudentId at the call site). */
  childId: string;
  /** Client-generated run id grouping this session's attempts
   *  (runIdRef.current at the call site) — same id session_completed would
   *  have used had the kid reached recap instead. */
  sessionRunId: string;
  /** Which trail row this session belonged to. Reuses
   *  SessionCompletedSubject — an exited run and a completed run are the
   *  same kind of thing, just with a different terminal event. */
  subject: SessionCompletedSubject;
  /** The item id on screen at the moment of exit, or null if the session
   *  had no item mounted yet (e.g. exit during initial load). */
  lastItemId: string | null;
  /** Milliseconds since the session's startedAtRef — how long the kid was
   *  in the session before exiting. */
  elapsedMs: number;
  /** True when the kid's current streak of wrong answers on the exited slot
   *  was non-zero at exit time (wrongStreakRef.current > 0 at the call
   *  site) — "quit after getting it wrong" vs. a plain mid-session exit.
   *  This is the signal canonicalEvidence.isAbandonment's exitSignal
   *  param consumes. */
  afterError: boolean;
}

/** Pure builder for the session-exit MilestoneEvent. Split out so the shape
 *  is unit-testable without a Supabase round-trip. A derivation signal, not
 *  a reward: rewardType null, child-invisible (no celebration is driven off
 *  a kid quitting), parent-visible (matches session_completed's posture — a
 *  parent-relevant "what happened today" signal). */
export function buildSessionExitEvent(args: SessionExitArgs): MilestoneEvent {
  return {
    childId: args.childId,
    eventType: "session_exit",
    sourceSkillIds: [],
    sourcePathId: null,
    rewardType: null,
    rewardVisibleToChild: false,
    rewardVisibleToParent: true,
    contextData: {
      subject: args.subject,
      runId: args.sessionRunId,
      lastItemId: args.lastItemId,
      elapsedMs: args.elapsedMs,
      afterError: args.afterError,
    },
  };
}

/** Fire the quit/abandonment marker. Call from the confirmed-Exit handler
 *  ONLY (never on dialog-open/Stay) and ONLY when the session hasn't
 *  already reached recap — screens guard this by checking their own
 *  sessionDoneRef before calling. Best-effort like logMilestoneEvent (retry
 *  + swallow); returns true on success. */
export async function logSessionExit(args: SessionExitArgs): Promise<boolean> {
  return logMilestoneEvent(buildSessionExitEvent(args));
}

// ── MA-624 — session_feedback (kid "how was that?" self-report) ────────

export interface SessionFeedbackArgs {
  childId: string;
  /** The run this feedback was given about (runIdRef.current at the recap's
   *  call site — same id session_completed/session_exit would have used). */
  sessionRunId: string;
  subject: SessionCompletedSubject;
  /** The focal skill the feedback is attributed to. Optional: a Mixed
   *  session (no single focal skill) or FoundationSession (deliberately
   *  never threads one — see FoundationSession.tsx's recap navigate call)
   *  still logs this event with an empty sourceSkillIds; only the ENGINE
   *  NUDGE (Progress.sessionFeedback / tierWithSessionFeedback) requires a
   *  skillId, and that gate lives in SessionRecap, not here. */
  skillId?: string;
  level: "easy" | "right" | "tricky";
}

/** Pure builder for the session_feedback MilestoneEvent, mirroring
 *  buildSessionExitEvent's "derivation signal, not a reward" posture:
 *  rewardType null, child-invisible, parent-visible. Split out so the shape
 *  is unit-testable without a Supabase round-trip. */
export function buildSessionFeedbackEvent(args: SessionFeedbackArgs): MilestoneEvent {
  return {
    childId: args.childId,
    eventType: "session_feedback",
    sourceSkillIds: args.skillId ? [args.skillId] : [],
    sourcePathId: null,
    rewardType: null,
    rewardVisibleToChild: false,
    rewardVisibleToParent: true,
    contextData: {
      runId: args.sessionRunId,
      subject: args.subject,
      level: args.level,
    },
  };
}

/** Fire the session_feedback marker. Call from SessionRecap's
 *  DifficultyFeedback onChange ONLY. Best-effort like logMilestoneEvent
 *  (retry + swallow); returns true on success. */
export async function logSessionFeedback(args: SessionFeedbackArgs): Promise<boolean> {
  return logMilestoneEvent(buildSessionFeedbackEvent(args));
}

// ── MA-300 — confirm_attempt (the MA-297 +1d confirm, decided in-app) ──

export interface ConfirmAttemptArgs {
  childId: string;
  /** The skill whose secure reach this attempt tests. */
  skillId: string;
  /** dayKey(t_reach) — the device-local day the skill reached secure. Part
   *  of the idempotency key (one row per reach). */
  reachDay: string;
  /** Whether that first next-day independent attempt was correct. */
  passed: boolean;
  /** MA-300 (RED S2) — progression.reachIsInWindow: the reach is provably
   *  the skill's real one, not an artifact of the 500-row mount window.
   *  founder_analytics_counts() counts only rows where this is true. */
  reachInWindow: boolean;
  /** The attempt's own timestamp, so the event's event_at is the moment of
   *  the confirm attempt even when it reaches the cloud later via the drain. */
  attemptAt: string;
}

/** Pure builder for the confirm_attempt MilestoneEvent. Aggregate-only
 *  telemetry: no reward, invisible to child and parent, and contextData
 *  carries no identifier (no runId, item id, name or answer) — only the
 *  pass bit, the reach day the idempotency key needs, and the in-window bit. */
export function buildConfirmAttemptEvent(args: ConfirmAttemptArgs): MilestoneEvent {
  return {
    childId: args.childId,
    eventType: "confirm_attempt",
    eventAt: args.attemptAt,
    sourceSkillIds: [args.skillId],
    sourcePathId: null,
    rewardType: null,
    rewardVisibleToChild: false,
    rewardVisibleToParent: false,
    contextData: {
      passed: args.passed,
      reachDay: args.reachDay,
      reachInWindow: args.reachInWindow,
    },
  };
}

/** Fire the confirm_attempt marker (cloud write; best-effort retry +
 *  swallow like every logXxx here). Call sites mirror it locally FIRST via
 *  appendLocalMilestone — see confirmAttemptTracker.emitConfirmAttempt. */
export async function logConfirmAttempt(args: ConfirmAttemptArgs): Promise<boolean> {
  return logMilestoneEvent(buildConfirmAttemptEvent(args));
}

// ── MA-246 (ADR-068) — chapter-completion ratchet marker ───────────────

interface ChapterCompleteArgs {
  /** The CANONICAL cloud students.id — same rule as every other milestone
   *  event (resolve via the canonical id at the call site). */
  childId: string;
  /** The chapter id (chapters.json `id`, e.g. "ch1") that just cleared
   *  chapterComplete for the first time for this child. Stored as
   *  sourcePathId so the default milestoneEventKey
   *  (`childId:chapter_complete:<sourcePathId>`) gives one-row-per-
   *  (child,chapter) idempotency for free. */
  chapterId: string;
  /** Every milestone + support skill in the chapter, so the parent
   *  dashboard (a future consumer) can join back to specific evidence —
   *  mirrors session_completed's skillIds convention. */
  sourceSkillIds: string[];
}

/** Pure builder for the chapter-complete MilestoneEvent. Split out so the
 *  shape is unit-testable without a Supabase round-trip. A derivation
 *  signal, not a reward: rewardType null, child-invisible (no celebration
 *  is driven off it today), parent-visible (matches the table default).
 *  `"chapter_complete"` was admitted by the ORIGINAL 0006 migration — no
 *  new CHECK-constraint widening needed. */
function buildChapterCompleteEvent(args: ChapterCompleteArgs): MilestoneEvent {
  return {
    childId: args.childId,
    eventType: "chapter_complete",
    sourceSkillIds: [...new Set(args.sourceSkillIds)],
    sourcePathId: args.chapterId,
    rewardType: null,
    rewardVisibleToChild: false,
    rewardVisibleToParent: true,
    contextData: { chapterId: args.chapterId },
  };
}

/** Fire the chapter-completion ratchet marker. Best-effort like
 *  logMilestoneEvent (retry + swallow); returns true on success. Call site
 *  (useTodaysMission) fires this ONLY for a chapter newly detected complete
 *  this call (progression.ts's newlyCompletedChapterIds) — the DB-level
 *  event_key upsert makes a repeat call for an already-credited chapter a
 *  safe no-op regardless. */
export async function logChapterComplete(args: ChapterCompleteArgs): Promise<boolean> {
  return logMilestoneEvent(buildChapterCompleteEvent(args));
}

// ── MA-176 — story-read marker ────────────────────────────────────────

interface StoryReadArgs {
  /** The CANONICAL cloud students.id — resolve via ensureActiveStudentId at
   *  the call site, NOT the local activeChildId (same rule as
   *  session-completed). */
  childId: string;
  /** The story slug that was finished. */
  slug: string;
  /** Client-generated id, fresh per StoryReader mount (so a genuine re-read,
   *  which remounts the reader, gets a NEW id and therefore its own row —
   *  see milestoneEventKey's story_read case). A retried write of the SAME
   *  finish (same mount) reuses the same id and collapses via the upsert. */
  readRunId: string;
  /** MA-184 — Tap-a-word AGGREGATE count for this one reader mount. NO
   *  per-word event stream (word identities are never persisted — see the
   *  MA-184 backlog item's telemetry rationale); omitted entirely when 0 so
   *  pre-MA-184 story_read rows and this field's absence mean the same
   *  thing on read. */
  wordTapCount?: number;
}

/** Pure builder for the story-read MilestoneEvent. Split out so the shape is
 *  unit-testable without a Supabase round-trip. Internal telemetry signal,
 *  not a reward grant (the reward for reading is the story itself, already
 *  granted at story_unlocked time — re-reading isn't a NEW reward, per the
 *  "one primary reward" principle): rewardType null, child-invisible,
 *  parent-visible (a parent-relevant "what happened" signal, matching the
 *  table default). */
export function buildStoryReadEvent(args: StoryReadArgs): MilestoneEvent {
  return {
    childId: args.childId,
    eventType: "story_read",
    sourceSkillIds: [],
    sourcePathId: null,
    rewardType: null,
    rewardVisibleToChild: false,
    rewardVisibleToParent: true,
    contextData: {
      slug: args.slug,
      runId: args.readRunId,
      ...(args.wordTapCount ? { wordTapCount: args.wordTapCount } : {}),
    },
  };
}

// ── MA-177 — favorite heart-toggle marker ────────────────────────────

interface FavoriteToggleArgs {
  /** The CANONICAL cloud students.id — resolve via ensureActiveStudentId at
   *  the call site, NOT the local activeChildId (same rule as
   *  session-completed / story-read). */
  childId: string;
  /** The story slug being favorited/unfavorited. */
  slug: string;
  /** Which direction this tap flips the heart to. */
  action: "set" | "unset";
  /** Client-generated id, fresh per TAP (not per mount — unlike readRunId, a
   *  slug can toggle back and forth many times in one reader session). A
   *  retried write of the SAME tap reuses this value and collapses via the
   *  upsert (see milestoneEventKey's favorite_set/favorite_unset case). */
  toggleRunId: string;
}

/** Pure builder for the favorite-toggle MilestoneEvent. Split out so the
 *  shape is unit-testable without a Supabase round-trip. Reversible/
 *  organizational signal, not a reward grant (a favorites list reorders
 *  books the kid already earned via the one primary reward system — it does
 *  not itself grant anything, per the "one primary reward" principle):
 *  rewardType null, child-invisible, parent-visible (matches story_read's
 *  posture). */
export function buildFavoriteToggleEvent(args: FavoriteToggleArgs): MilestoneEvent {
  return {
    childId: args.childId,
    eventType: args.action === "set" ? "favorite_set" : "favorite_unset",
    sourceSkillIds: [],
    sourcePathId: null,
    rewardType: null,
    rewardVisibleToChild: false,
    rewardVisibleToParent: true,
    contextData: {
      slug: args.slug,
      runId: args.toggleRunId,
    },
  };
}

// ── MA-256 slice 1 — book-opened marker (delight instrumentation) ───

interface BookOpenedArgs {
  /** The CANONICAL cloud students.id — resolve via ensureActiveStudentId at
   *  the call site, NOT the local activeChildId (same rule as every other
   *  milestone write). */
  childId: string;
  /** The story slug that was opened. */
  slug: string;
  /** Client-generated id, fresh per StoryReader mount (readRunIdRef at the
   *  call site — the SAME id MA-176's story_read event for this mount uses).
   *  A retried write of the SAME mount's open reuses this value and collapses
   *  via the upsert; a GENUINE second open (a fresh mount, e.g. a later
   *  navigation back into the same book) gets its own id and therefore its
   *  own row — this repetition, per (child, slug), IS how a voluntary
   *  reopen is detected (see bookOpenLog.ts). */
  openRunId: string;
  /** Navigation origin — which shelf surface the tap came from. "direct"
   *  covers a hard refresh / deep link / any load where `from` wasn't
   *  threaded. NOTE: this is a NAV-origin namespace, distinct from the
   *  deferred family-(c) reward-origin `source` enum (library|favorites|
   *  unlock) — do not conflate when that slice ships. */
  source: "library" | "read" | "direct";
}

/** Pure builder for the book-opened MilestoneEvent. Split out so the shape is
 *  unit-testable without a Supabase round-trip. Internal telemetry signal,
 *  not a reward grant (opening isn't itself rewarded — the reward already
 *  happened at story_unlocked time): rewardType null, child-invisible,
 *  parent-visible (matches story_read/favorite_* posture). KNOWN CAVEAT: like
 *  every mount-based milestone in this file, an unexpected remount (hard
 *  refresh, backgrounded-tab reload, PWA memory-pressure reload mid-read)
 *  produces an indistinguishable extra "open" with zero kid intent — this is
 *  inherent to the mount-as-proxy technique (story_read/favorite_* share it)
 *  and is accepted, not silently assumed away. */
export function buildBookOpenedEvent(args: BookOpenedArgs): MilestoneEvent {
  return {
    childId: args.childId,
    eventType: "book_opened",
    sourceSkillIds: [],
    sourcePathId: null,
    rewardType: null,
    rewardVisibleToChild: false,
    rewardVisibleToParent: true,
    contextData: {
      slug: args.slug,
      runId: args.openRunId,
      source: args.source,
    },
  };
}

// ── MA-256 book-impression slice — shelf-render exposure marker ─────

interface BookImpressionArgs {
  /** The CANONICAL cloud students.id — resolve via ensureActiveStudentId at
   *  the call site, NOT the local activeChildId (same rule as every other
   *  milestone write). */
  childId: string;
  /** The story slug that was shown to the kid. */
  slug: string;
  /** Which shelf surface rendered this tile. Only "unlock-choice" has a
   *  live producer this slice. */
  shelfSurface: ShelfSurface;
  /** 0-indexed tile position within the surface's rendered set at
   *  impression time (e.g. 0/1/2 for the 3 unlock-choice tiles). */
  position: number;
  /** Whether the story was locked (not yet owned) or unlocked at impression
   *  time. */
  lockState: "locked" | "unlocked";
  /** Client-generated id, unique PER TILE (not shared across the surface's
   *  tiles) — a shared id would collapse multiple real impressions in the
   *  SAME round into one event_key. A retried write of the SAME tile's
   *  SAME showing reuses this value and collapses via the upsert; a genuine
   *  re-showing (e.g. a fresh mount of the surface) gets its own id and
   *  therefore its own row — this repetition, per (child, slug, surface),
   *  IS how exposure_count is derived (see bookImpressionLog.ts; it is
   *  computed, not a wire field, to preserve the table's append-only/
   *  no-UPDATE invariant). */
  runId: string;
}

/** Pure builder for the book-impression MilestoneEvent. Split out so the
 *  shape is unit-testable without a Supabase round-trip. Internal
 *  telemetry signal, not a reward grant: rewardType null, child-invisible,
 *  parent-visible (matches book_opened/story_read/favorite_* posture).
 *  contextData is deliberately minimal (slug/shelfSurface/position/
 *  lockState/runId only — no title, no other candidates' slugs) — same
 *  privacy fence as every other milestone event (aggregate/first-party,
 *  no PII, no page text). */
export function buildBookImpressionEvent(args: BookImpressionArgs): MilestoneEvent {
  return {
    childId: args.childId,
    eventType: "book_impression",
    sourceSkillIds: [],
    sourcePathId: null,
    rewardType: null,
    rewardVisibleToChild: false,
    rewardVisibleToParent: true,
    contextData: {
      slug: args.slug,
      shelfSurface: args.shelfSurface,
      position: args.position,
      lockState: args.lockState,
      runId: args.runId,
    },
  };
}

// ── MA-892 — badge_earned writer ─────────────────────────────────────

/** The family a badge id belongs to (MA-891 ids: `skill:…`, `chapter:…`, `days:…`). */
export type BadgeEventFamily = "skill" | "chapter" | "days";

export interface BadgeEarnedArgs {
  /** The child id the row is written for (the canonical students.id for a
   *  cloud write; the drain re-stamps a locally mirrored row). */
  childId: string;
  /** A badge RECORD id from the MA-891 model's `earnedIds`: `skill:<id>:silver`,
   *  `skill:<id>:gold`, `chapter:<trophy>`, `days:<n>` or `days:<n>#<k>`. */
  badgeId: string;
}

/** The family of a badge record id, or null for an id that is not one. */
export function badgeFamilyOf(badgeId: string): BadgeEventFamily | null {
  const head = badgeId.split(":")[0];
  return head === "skill" || head === "chapter" || head === "days" ? head : null;
}

/** Pure builder for one badge_earned row (KID-JOURNEY D-KJ-11): reward_type
 *  badge, visible to the child and the parent, context_data { badgeId, family }.
 *  event_at is left to the server default (`now()`), so the cloud row carries
 *  the server clock; the local mirror stamps its own copy on append. A skill
 *  badge names its skill in source_skill_ids. */
export function buildBadgeEarnedEvent(args: BadgeEarnedArgs): MilestoneEvent {
  const family = badgeFamilyOf(args.badgeId);
  if (!family) throw new Error(`buildBadgeEarnedEvent: not a badge id: ${args.badgeId}`);
  const skillId = family === "skill" ? args.badgeId.split(":")[1] : null;
  return {
    childId: args.childId,
    eventType: "badge_earned",
    sourceSkillIds: skillId ? [skillId] : [],
    sourcePathId: null,
    rewardType: "badge",
    rewardVisibleToChild: true,
    rewardVisibleToParent: true,
    contextData: { badgeId: args.badgeId, family },
  };
}

/** MA-892 — every milestone row of the given types for a child, paged until
 *  the server returns a short page (a COMPLETE read, unlike the fixed-window
 *  reader below, whose 100-row default is too few to count 100 practice
 *  days). Most-recent first, ties broken by id so pages never overlap.
 *  Throws on a DB error (the caller decides how to degrade). `client` is
 *  test-only. */
export async function fetchAllMilestoneEventsOfTypesOrThrow(
  childId: string,
  eventTypes: MilestoneEventType[],
  // 500: every existing reader already asks for 500-row windows, so the
  // server's max-rows cap (PostgREST default 1000) never shortens a page and
  // ends the loop early.
  pageSize = 500,
  client: Pick<typeof supabase, "from"> = supabase,
): Promise<MilestoneEvent[]> {
  const out: MilestoneEvent[] = [];
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await client
      .from(TABLE)
      .select("*")
      .eq("child_id", childId)
      .in("event_type", eventTypes)
      .order("event_at", { ascending: false })
      .order("id", { ascending: true })
      .range(from, from + pageSize - 1);
    if (error) throw error;
    const rows = (data ?? []) as Record<string, unknown>[];
    out.push(...rows.map(fromRow));
    if (rows.length < pageSize) return out;
  }
}

/** Fetch a child's milestone events, most-recent first. Mirrors
 *  attemptLogger.fetchAllAttemptsForChild (snake→camel via fromRow, error →
 *  []). `since` filters to events at/after that instant (the trail passes
 *  local midnight so the query only returns today's markers). Consumed by
 *  PathHome's useTrailMilestones → completedSubjectsToday. */
/** Throwing core: surfaces a DB error instead of swallowing to []. The
 *  parent dashboard uses this so a failed fetch shows a retry surface
 *  rather than a false "no milestones yet" empty state.
 *  `eventTypes` (MA-176): optional server-side type filter. The earned-shelf
 *  callers (library/ReadTab/StoryReader/DayCompleteCelebration) read an
 *  UNBOUNDED (`since` omitted), fixed-`limit` window — as story_read volume
 *  grows, an unfiltered fetch risks aging a real story_unlocked event out of
 *  that window. Appended last (not inserted) so every existing positional
 *  call site (`childId, since, limit`) is unaffected. */
export async function fetchMilestoneEventsForChildOrThrow(
  childId: string,
  since?: Date,
  limit = 100,
  eventTypes?: MilestoneEventType[],
): Promise<MilestoneEvent[]> {
  let query = supabase.from(TABLE).select("*").eq("child_id", childId);
  if (since) query = query.gte("event_at", since.toISOString());
  if (eventTypes && eventTypes.length > 0) query = query.in("event_type", eventTypes);
  const { data, error } = await query
    .order("event_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []).map(fromRow);
}

/** Swallowing wrapper: returns [] on error (kid-facing trail reads tolerate
 *  a transient miss and re-fetch). Existing callers keep this behavior. */
export async function fetchMilestoneEventsForChild(
  childId: string,
  since?: Date,
  limit = 100,
  eventTypes?: MilestoneEventType[],
): Promise<MilestoneEvent[]> {
  try {
    return await fetchMilestoneEventsForChildOrThrow(childId, since, limit, eventTypes);
  } catch (error) {
    console.error("[milestoneLogger] fetchMilestoneEventsForChild failed:", error);
    return [];
  }
}

// Test-only exports for the round-trip + builder coverage suites.
// P-EXIT-SESSION wired the FIRST reader (fetchMilestoneEventsForChild →
// PathHome trail), so the "no producer-no-consumer" deferral note from
// P-METADATA-EXPAND-A no longer applies to session_completed. The other
// six event types still have no derivation site (deferred to
// P-METADATA-EXPAND-B / the parent dashboard).
//
// __testFireCapture: test-only callable that invokes captureSupabaseError
// directly so tests can verify the Sentry hook fires (via console.error
// interception — same pattern as sentry.test.mjs) without needing a live
// Supabase connection to reach the permanent-failure branch.
export const __test = {
  toRow,
  fromRow,
  buildSessionCompletedEvent,
  buildSessionExitEvent,
  buildChapterCompleteEvent,
  buildStoryReadEvent,
  buildFavoriteToggleEvent,
  buildBookOpenedEvent,
  buildBookImpressionEvent,
  milestoneEventKey,
  buildBadgeEarnedEvent,
  fireCapture: (label: string, error: unknown, context?: Record<string, unknown>) =>
    captureSupabaseError(label, error, context),
  isBreakerExemptEventType: (t: MilestoneEventType) => BREAKER_EXEMPT_EVENT_TYPES.has(t),
  // MA-300 — reset the per-session quiet latch between test scenarios.
  resetQuietLatch: () => quietTypesStuckThisSession.clear(),
};
